/**
 * Servicio para gestionar funciones comunes de los componentes de checklist
 */

export default {
  /**
   * Obtiene el icono correspondiente a un valor de parámetro
   * @param {String} value - Valor del parámetro ('checked', 'not_required', 'pending')
   * @returns {String} - Nombre del icono de Material Design
   */
  getParameterIcon(value) {
    const icons = {
      'checked': 'check_circle',
      'not_required': 'remove_circle',
      'pending': 'access_time'
    }
    return icons[value] || 'access_time'
  },

  /**
   * Obtiene el color para el icono de un parámetro
   * @param {String} value - Valor del parámetro ('checked', 'not_required', 'pending')
   * @returns {String} - Nombre del color
   */
  getParameterIconColor(value) {
    const colors = {
      'checked': 'success',
      'not_required': 'grey',
      'pending': 'warning'
    }
    return colors[value] || 'grey'
  },

  /**
   * Obtiene el icono para una sección
   * @param {Object} section - Objeto de sección
   * @returns {String} - Nombre del icono
   */
  getSectionIcon(section) {
    return section.icon || 'assignment'
  },

  /**
   * Obtiene el color del icono para una sección basado en su estado
   * @param {Object} section - Objeto de sección
   * @param {Boolean} hasValueChanges - Indica si hay cambios en los valores
   * @param {Boolean} allParametersCompleted - Indica si todos los parámetros están completados
   * @returns {String} - Nombre del color
   */
  getSectionIconColor(section, hasValueChanges, allParametersCompleted) {
    if (hasValueChanges) {
      return 'error'
    }

    if (allParametersCompleted && section.value === 'checked') {
      return 'success'
    }

    if (section.isDisabled) {
      return 'contrast_medium'
    }

    return section.value === 'checked' ? 'success' : 'contrast_high'
  },

  /**
   * Obtiene el valor de un campo del proceso
   * @param {Object} processInfo - Información del proceso
   * @param {String} field - Campo a obtener
   * @returns {*} - Valor del campo o 'N/A' si no existe
   */
  getProcessValue(processInfo, field) {
    if (!processInfo || !field) return 'N/A'

    try {
      const value = field.split('.').reduce((obj, key) => obj && obj[key], processInfo)
      return value !== undefined ? value : 'N/A'
    } catch (error) {
      console.warn('Error al obtener valor del proceso:', error)
      return 'N/A'
    }
  },

  /**
   * Obtiene los cambios en los valores de un parámetro
   * @param {Object} parameter - Parámetro a verificar
   * @param {Object} processInfo - Información del proceso
   * @returns {Array} - Lista de campos con cambios
   */
  getValueChanges(parameter, processInfo) {
    // Verificar que existe extraInfo antes de hacer Object.keys
    if (!parameter || !parameter.extraInfo || typeof parameter.extraInfo !== 'object') {
      return []
    }

    // Solo verificar cambios si el parámetro está marcado como 'checked' y tenemos processInfo
    if (parameter.value !== 'checked' || !processInfo) {
      return []
    }

    try {
      const changedFields = []

      Object.keys(parameter.extraInfo).forEach(field => {
        const savedValue = parameter.extraInfo[field]
        const currentValue = this.getProcessValue(processInfo, field)

        // Comparar valores (considerar null y undefined como equivalentes)
        const normalizedSaved = savedValue === null || savedValue === undefined ? null : savedValue
        const normalizedCurrent = currentValue === 'N/A' || currentValue === null || currentValue === undefined ? null : currentValue

        // Solo agregar a la lista de cambios si realmente hay diferencia
        if (normalizedSaved !== normalizedCurrent) {
          changedFields.push(field)
        }
      })

      return changedFields
    } catch (error) {
      console.warn('Error al obtener cambios de valor:', error)
      return []
    }
  },

  /**
   * Comprueba si un parámetro tiene cambios en sus valores
   * @param {Object} parameter - Parámetro a verificar
   * @param {Object} processInfo - Información del proceso
   * @returns {Boolean} - True si hay cambios, False si no los hay
   */
  parameterHasValueChanges(parameter, processInfo) {
    return this.getValueChanges(parameter, processInfo).length > 0
  },

  /**
   * Comprueba si una sección tiene cambios en alguno de sus parámetros
   * @param {Object} section - Sección a verificar
   * @param {Object} processInfo - Información del proceso
   * @returns {Boolean} - True si hay cambios, False si no los hay
   */
  sectionHasValueChanges(section, processInfo) {
    if (!section || !section.parameters || !processInfo) {
      return false
    }

    return Object.values(section.parameters).some(parameter => {
      return this.parameterHasValueChanges(parameter, processInfo)
    })
  }
}
